import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, AlertTriangle, Check, X, Mail, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import loginHero from '@/assets/login-hero.jpg';

function getPasswordStrength(pwd: string) {
  const checks = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, passed, strong: passed >= 4 && checks.length };
}

const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null;
const buildVersion = buildTime
  ? new Date(buildTime).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })
  : null;

export function SetupScreen() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(reg => {
      const checkWaiting = () => {
        if (reg.waiting) {
          setWaitingSW(reg.waiting);
          setUpdateAvailable(true);
        }
      };
      checkWaiting();
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingSW(newWorker);
            setUpdateAvailable(true);
          }
        });
      });
    });
  }, []);

  const handleUpdate = () => {
    if (waitingSW) {
      waitingSW.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };
  const { setup, login } = useAuth();
  const [mode, setMode] = useState<'signup' | 'login' | 'forgot'>('signup');
  const [email, setEmail] = useState('');
  const [value, setValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const strength = useMemo(() => getPasswordStrength(value), [value]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (mode === 'forgot') {
      setLoading(true);
      try {
        const { error: resetError } = await (await import('@/integrations/supabase/client')).supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (resetError) throw resetError;
        setResetSent(true);
      } catch (err: any) {
        setError(err?.message || 'Failed to send reset email');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (mode === 'signup') {
      setError('Sign-ups are temporarily suspended during beta testing. Please check back in April 2026.');
      setLoading(false);
      return;
    }
    if (!value) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      await login(email, value);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ met, label }: { met: boolean; label: string }) => (
    <div className="flex items-center gap-1.5 text-xs">
      {met ? <Check className="h-3 w-3 text-primary" /> : <X className="h-3 w-3 text-muted-foreground" />}
      <span className={met ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );

  return (
    <main className="flex min-h-screen items-start justify-center bg-background px-4 py-3 relative overflow-hidden">
      {/* Hero background image — no overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={loginHero}
          alt=""
          className="h-full w-full object-cover object-top"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm relative z-10 rounded-2xl bg-background/40 backdrop-blur-xl p-4 shadow-lg border border-border/50"
      >
        {/* Update available banner */}
        <AnimatePresence>
          {updateAvailable && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5"
            >
              <div className="flex items-center gap-2 text-sm text-primary font-medium">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Update available
              </div>
              <button
                onClick={handleUpdate}
                className="rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Reload
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="mb-2 flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground leading-tight">AussiBooks</h1>
            <p className="text-[11px] text-muted-foreground leading-tight">Private money manager for Australians</p>
          </div>
        </div>

        {/* Beta testing banner */}
        <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-600 dark:text-amber-400 text-sm mb-1">Beta Testing in Progress</p>
              <p className="text-foreground/80 text-xs leading-snug">
                New sign-ups are temporarily suspended while we complete beta testing. The app is expected to launch publicly in <span className="font-semibold">April 2026</span>.
              </p>
            </div>
          </div>
        </div>

        {/* App blurb */}
        <div className="mb-3 rounded-xl border bg-card/60 p-2.5">
          <p className="text-foreground/75 text-xs leading-snug mb-1.5">
            Budgets, bills, transactions &amp; savings goals — private, secure, with optional live bank sync via Pro.
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded-lg bg-muted/60 px-2 py-1.5 space-y-0">
              <p className="font-semibold text-foreground text-[10px] uppercase tracking-wide mb-0.5">Free</p>
              {['Manual transactions', 'Budgets & bills', 'Savings goals', 'Tax wizard'].map(f => (
                <div key={f} className="flex items-center gap-1">
                  <Check className="h-2.5 w-2.5 text-primary shrink-0" />
                  <span className="text-xs text-muted-foreground leading-tight">{f}</span>
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-primary/8 border border-primary/20 px-2 py-1.5 space-y-0">
              <p className="font-semibold text-primary text-[10px] uppercase tracking-wide mb-0.5">Pro ✦</p>
              {['Everything in Free', 'Live bank sync', 'Auto-import', 'Priority support'].map(f => (
                <div key={f} className="flex items-center gap-1">
                  <Check className="h-2.5 w-2.5 text-primary shrink-0" />
                  <span className="text-xs text-muted-foreground leading-tight">{f}</span>
                </div>
              ))}
              <p className="text-xs text-primary font-medium pt-0.5">From $4.50/mo</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <h2 className="text-base font-semibold leading-none tracking-tight">
              {mode === 'signup' ? 'Create your account' : mode === 'login' ? 'Welcome back' : 'Reset password'}
            </h2>
            <CardDescription className="text-xs">
              {mode === 'signup'
                ? 'Your password encrypts all data locally and secures your cloud account.'
                : mode === 'login'
                ? 'Sign in with your existing account to continue.'
                : 'Enter your email and we\'ll send you a reset link.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {mode === 'forgot' ? (
              resetSent ? (
                <div className="space-y-3">
                  <p className="text-sm text-primary font-medium">Reset link sent! Check your email inbox.</p>
                  <Button variant="outline" className="w-full" onClick={() => { setMode('login'); setResetSent(false); }}>
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-2.5">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-11 pl-10 text-base"
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="h-11 w-full text-base" disabled={loading || !email}>
                    {loading ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                </form>
              )
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2.5">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-11 pl-10 text-base"
                    autoFocus
                  />
                </div>

                <Input
                  type="password"
                  placeholder={mode === 'signup' ? 'Master password' : 'Password'}
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  className="h-11 text-base"
                />

                {mode === 'signup' && value.length > 0 && (
                  <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/50 p-2.5">
                    <Rule met={strength.checks.length} label="8+ characters" />
                    <Rule met={strength.checks.upper} label="Uppercase" />
                    <Rule met={strength.checks.lower} label="Lowercase" />
                    <Rule met={strength.checks.number} label="Number" />
                    <Rule met={strength.checks.special} label="Special char" />
                  </div>
                )}

                {mode === 'signup' && (
                  <Input
                    type="password"
                    placeholder="Confirm password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="h-11 text-base"
                  />
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button
                  type="submit"
                  className="h-11 w-full text-base"
                  disabled={loading || (mode === 'signup' && (!strength.strong || !email)) || (mode === 'login' && (!email || !value))}
                >
                  {loading
                    ? (mode === 'signup' ? 'Creating account…' : 'Signing in…')
                    : (mode === 'signup' ? 'Create Account' : 'Sign In')}
                </Button>

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); }}
                    className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline"
                  >
                    Forgot your password?
                  </button>
                )}
              </form>
            )}

            {mode === 'signup' && (
              <p className="mt-3 text-center text-xs text-muted-foreground">
                A verification email will be sent. You can use the app immediately — verify later to unlock Pro features.
              </p>
            )}

            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => { setMode(m => m === 'signup' ? 'login' : 'signup'); setError(''); setValue(''); setConfirm(''); setResetSent(false); }}
                className="text-sm text-primary hover:underline"
              >
                {mode === 'signup' ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
              </button>
            </div>

            {mode === 'signup' && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/30 p-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-xs font-medium text-destructive">
                  If you forget your password, your data cannot be recovered. Export backups regularly.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Version footer */}
        {buildVersion && (
          <p className="mt-2 text-center text-[10px] text-muted-foreground/60">
            Build {buildVersion}
          </p>
        )}
      </motion.div>
    </main>
  );
}
